'use client';

import type { CaseResult } from '@/lib/smart-finder/types';

interface CaseResultCardProps {
  caseResult: CaseResult;
  onTrack: () => void;
  isAuthenticated?: boolean;
  isLimitedPreview?: boolean;
  /** A verified identity match from the recent case index, not a live status lookup. */
  isIndexedMatch?: boolean;
  /** Keep the filing year visible in an otherwise-masked case number.
   *  Candidate-resolution results may come from a different year than the
   *  visitor intended; the year is what tells them whether this is their case. */
  revealFilingYear?: boolean;
  // Overrides the default conversion CTA.
  // Used on the demo dashboard to show "Upgrade to see your case".
  ctaLabel?: string;
}

/** Replace digit runs with bullet characters of the same length. */
function maskDigits(str: string): string {
  return str.replace(/\d+/g, (m) => '•'.repeat(m.length));
}

/** Mask digits but leave a trailing 4-digit filing year readable, e.g.
 *  "WP/31646/2022" → "WP/•••••/2022". The year must sit in its own trailing
 *  segment (after a separator) so a digit-glued case number is never partly
 *  revealed; anything else falls back to full masking. */
export function maskDigitsKeepingYear(str: string): string {
  const trimmed = str.trimEnd();
  const year = trimmed.match(/[/\s-](\d{4})$/)?.[1];
  if (!year) return maskDigits(str);
  return `${maskDigits(trimmed.slice(0, trimmed.length - year.length))}${year}`;
}

export default function CaseResultCard({
  caseResult,
  onTrack,
  isAuthenticated = false,
  isLimitedPreview = false,
  isIndexedMatch = false,
  revealFilingYear = false,
  ctaLabel,
}: CaseResultCardProps) {
  const parties = [caseResult.petitioner, caseResult.respondent].filter(Boolean).join(' vs ');
  const canShowLimitedDetails = isAuthenticated || isLimitedPreview;

  // Build case number display — avoid double-formatting if case_number already contains slashes
  const rawCaseNumber = caseResult.case_number?.includes('/')
    ? caseResult.case_number
    : [caseResult.case_type, caseResult.case_number, caseResult.case_year]
        .filter(Boolean)
        .join('/');

  // Non-auth visitors see the full party identity for confidence, while digits
  // remain masked so the exact CNR/case number stays behind the tracking gate.
  const caseNumberDisplay = canShowLimitedDetails
    ? rawCaseNumber
    : rawCaseNumber
      ? revealFilingYear
        ? maskDigitsKeepingYear(rawCaseNumber)
        : maskDigits(rawCaseNumber)
      : rawCaseNumber;

  // Build court/location display
  const locationParts = [caseResult.court_name, caseResult.district, caseResult.state].filter(Boolean);
  const locationDisplay = locationParts.join(', ');

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      {/* Top row: case number + status */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          {caseNumberDisplay && (
            <h3 className="font-semibold text-gray-900 text-base truncate">
              {caseNumberDisplay}
            </h3>
          )}
          {caseResult.cnr_number && (
            <p className="font-mono text-xs text-primary-400 mt-0.5">
              {canShowLimitedDetails ? caseResult.cnr_number : maskDigits(caseResult.cnr_number)}
            </p>
          )}
        </div>
        {caseResult.status && !caseResult.is_partial && !isIndexedMatch && (
          <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${
            caseResult.status.toLowerCase().includes('disposed') || caseResult.status.toLowerCase().includes('decided')
              ? 'bg-gray-100 text-gray-600'
              : 'bg-india-green-50 text-india-green-600'
          }`}>
            {caseResult.status}
          </span>
        )}
      </div>

      {caseResult.is_partial && !isIndexedMatch && (
        <div className="text-xs text-accent-700 bg-accent-50 px-3 py-1.5 rounded-lg font-medium mb-3 border border-accent-100">
          {isLimitedPreview ? 'Limited preview - subscribe for full verified details' : 'Based on your description - track to get full verified details'}
        </div>
      )}

      {isLimitedPreview && (
        <div className="text-xs text-primary-700 bg-primary-50 px-3 py-1.5 rounded-lg font-medium mb-3 border border-primary-100">
          Limited free-search result
        </div>
      )}

      {/* Details grid */}
      <div className="space-y-2 text-sm">
        {locationDisplay && (
          <div className="flex items-center gap-2.5 text-gray-600">
            <svg className="w-4 h-4 text-primary-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span>{locationDisplay}</span>
          </div>
        )}

        {parties && (
          <div className="flex items-center gap-2.5 text-gray-600">
            <svg className="w-4 h-4 text-primary-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{parties}</span>
          </div>
        )}

        {/*
          additional_details is intentionally NOT rendered.
          Some scrapers pack internal session tokens (itat_cid, sat_data_id,
          nclt_filing_no) into this field; surfacing it leaks plumbing and
          gives nothing the user understands. The router-side fix lives in
          src/lib/smart-finder/scraper-router.ts but the search droplet
          serves a cached image, so this UI guard is the safety net.
        */}

        {/* Inline metadata row — only for authenticated users; non-auth visitors
            are nudged to sign in instead of getting next-hearing for free. */}
        {canShowLimitedDetails && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
            {!isIndexedMatch && caseResult.next_hearing_date && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <svg className="w-3.5 h-3.5 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Next: {caseResult.next_hearing_date}
              </span>
            )}
            {!isIndexedMatch && caseResult.last_updated && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                Updated: {new Date(caseResult.last_updated).toLocaleDateString('en-IN')}
              </span>
            )}
            {isAuthenticated && caseResult.judge_name && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                Judge: {caseResult.judge_name}
              </span>
            )}
            {isAuthenticated && caseResult.filing_date && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                Filed: {caseResult.filing_date}
              </span>
            )}
          </div>
        )}
      </div>

      {caseResult.clarification && !isIndexedMatch && (
        <p className="text-xs text-gray-400 italic mt-2">
          {caseResult.clarification}
        </p>
      )}

      {/* CTA — auth users get track flow; non-auth users get a sign-in nudge
          (the underlying handler already routes through /login when not auth) */}
      <button
        onClick={onTrack}
        className="w-full mt-4 bg-primary text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-primary-700 active:scale-[0.99] transition-all flex items-center justify-center gap-2"
      >
        {ctaLabel
          ?? (isLimitedPreview
            ? 'Start tracking this case'
            : isAuthenticated
            ? (caseResult.is_partial ? 'Add case to track' : 'Track this case free')
            : 'Track this case free')}
        <svg className="w-4 h-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </button>
    </div>
  );
}
